import { Close, InfoOutlined } from '@mui/icons-material';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Result from 'components/v2/common/Result';

import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import { getDocumentDescriptionHtml, hasDocumentDescription, linkifyHtml } from 'utils/v2/document';

export interface DocumentDescriptionSource {
    document_name: string;
    explanation?: string | null;
    description_en?: string | null;
    description_fr?: string | null;
}

interface DocumentDescriptionModalProps {
    document: DocumentDescriptionSource;
    onClose: () => void;
}

interface DocumentDescriptionInfoButtonProps {
    document: DocumentDescriptionSource;
    onClick: () => void;
}

export const DocumentDescriptionInfoButton = ({
    document,
    onClick,
}: DocumentDescriptionInfoButtonProps) => {
    const { translate } = useTranslate();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);

    if (!hasDocumentDescription(document, language)) {
        return null;
    }

    return (
        <Tooltip title={translate('description')} placement="top">
            <IconButton
                size="small"
                sx={{ flexShrink: 0 }}
                onClick={(event) => {
                    event.stopPropagation();
                    onClick();
                }}
            >
                <InfoOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
            </IconButton>
        </Tooltip>
    );
};

const DocumentDescriptionModal = ({ document, onClose }: DocumentDescriptionModalProps) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);

    const rawHtml = getDocumentDescriptionHtml(document, language);
    const descriptionHtml = useMemo(() => (rawHtml ? linkifyHtml(rawHtml) : null), [rawHtml]);

    return (
        <Dialog open fullWidth maxWidth="sm" fullScreen={isMobile} scroll="paper" onClose={onClose}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    pr: 1,
                }}
            >
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                    {translate('description')}
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {document.document_name}
                    </Typography>
                </Stack>
                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label={translate('close')}
                    sx={{ flexShrink: 0 }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {descriptionHtml ? (
                    <Box
                        sx={{
                            typography: 'body2',
                            color: 'text.primary',
                            wordBreak: 'break-word',
                            '& p': { m: 0, mb: 1.5 },
                            '& p:last-child': { mb: 0 },
                            '& a': { color: 'primary.main' },
                            '& ul, & ol': { pl: 2.5, mb: 1.5 },
                        }}
                        dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                    />
                ) : (
                    <Result title={translate('noDescriptionAvailable')} description="" />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DocumentDescriptionModal;
